import type { HierarchyLevel, HierarchyNode, HierarchyScope, HierarchyScopeView } from '../models/hierarchyScope';
import { HIERARCHY_LEVELS, SCOPE_FIELD, parentLevel } from '../models/hierarchyScope';

/**
 * The hierarchy the mocked dropdowns offer: two entities (the shell's picker
 * chooses one), two units of the first so a selection can span parents, and a
 * Business Unit name repeated under both entities, so the parent code in the
 * label is what tells them apart. Name order puts Procurement → Direct
 * Materials → Purchase Team first: the defaults a form opens on.
 */
interface MockNode {
  id: number;
  parentId: number | null;
  code: string;
  name: string;
}

const NODES: Readonly<Record<HierarchyLevel, readonly MockNode[]>> = {
  entities: [
    { id: 1, parentId: null, code: 'ENT-001', name: 'AIT Global Pvt Ltd' },
    { id: 2, parentId: null, code: 'ENT-002', name: 'XYZ Limited' },
  ],
  'business-units': [
    { id: 11, parentId: 1, code: 'BU-001', name: 'Procurement' },
    { id: 12, parentId: 1, code: 'BU-002', name: 'Sales' },
    { id: 21, parentId: 2, code: 'BU-003', name: 'Procurement' },
  ],
  divisions: [
    { id: 111, parentId: 11, code: 'DIV-001', name: 'Direct Materials' },
    { id: 112, parentId: 11, code: 'DIV-002', name: 'Indirect Materials' },
    { id: 121, parentId: 12, code: 'DIV-004', name: 'Accounts' },
    { id: 211, parentId: 21, code: 'DIV-003', name: 'Sourcing' },
  ],
  departments: [
    { id: 1111, parentId: 111, code: 'DEPT-001', name: 'Purchase Team' },
    { id: 1121, parentId: 112, code: 'DEPT-002', name: 'Stores' },
    { id: 2111, parentId: 211, code: 'DEPT-003', name: 'Vendor Desk' },
  ],
};

function labelled(level: HierarchyLevel, node: MockNode): HierarchyNode {
  const above = parentLevel(level);
  const parentCode = above ? (NODES[above].find((parent) => parent.id === node.parentId)?.code ?? null) : null;
  return {
    id: node.id,
    code: node.code,
    name: node.name,
    parentId: node.parentId,
    parentCode,
    label: `${node.code} + ${node.name}${parentCode ? ` (${parentCode})` : ''}`,
  };
}

/** A level's values under any of the parents, in name order -- the endpoint's answer. */
export async function mockHierarchyOptions(
  level: HierarchyLevel,
  parentIds: readonly number[],
): Promise<HierarchyNode[]> {
  return NODES[level]
    .filter((node) => level === 'entities' || (node.parentId !== null && parentIds.includes(node.parentId)))
    .map((node) => labelled(level, node))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** A selection labelled, as a read-back mapping. */
export function mockDescribeScope(scope: HierarchyScope): HierarchyScopeView {
  const [entities, units, divisions, departments] = HIERARCHY_LEVELS.map((level) =>
    scope[SCOPE_FIELD[level]]
      .map((id) => NODES[level].find((node) => node.id === id))
      .filter((node): node is MockNode => node !== undefined)
      .map((node) => labelled(level, node)),
  );
  return {
    legalEntities: entities ?? [],
    businessUnits: units ?? [],
    divisions: divisions ?? [],
    departments: departments ?? [],
  };
}

/** The Category master's product categories behind `env.ENABLE_MOCKS`, shared by every entity. */
export const MOCK_TEMPLATE_CATEGORIES = [
  { id: 601, code: 'CAT-001', name: 'IT Hardware', label: 'CAT-001 - IT Hardware' },
  { id: 602, code: 'CAT-002', name: 'Office Supplies', label: 'CAT-002 - Office Supplies' },
] as const;

export async function mockTemplateCategories(_legalEntityIds: readonly number[]) {
  return Promise.resolve(MOCK_TEMPLATE_CATEGORIES.map((category) => ({ ...category })));
}